import { QueryClient } from '@tanstack/react-query';
import { bffDiscoverQueryKeys, bffUserProfileQueryKeys } from 'app/network/keys';

export const WINDOW_FOCUS_REFETCH_MIN_AGE_MS = 30_000;

interface WindowFocusQuery {
  queryKey: readonly unknown[];
  state: { dataUpdatedAt: number };
}

const isSubQueryOf = (query: WindowFocusQuery, keyMap: Record<string, () => readonly unknown[]>) => {
  return Object.values(keyMap).some((fn) => {
    const bffQueryKey = fn();
    // A query key can't be a sub-query if it's shorter than the base key.
    if (query.queryKey.length < bffQueryKey.length) {
      return false;
    }

    // Check if the query key starts with the BFF sub-query key.
    return bffQueryKey.every((part, index) => JSON.stringify(part) === JSON.stringify(query.queryKey[index]));
  });
};

export function shouldRefetchOnWindowFocus(query: WindowFocusQuery): boolean {
  // Do not refetch queries included in BFF queries.
  if (isSubQueryOf(query, bffDiscoverQueryKeys) || isSubQueryOf(query, bffUserProfileQueryKeys)) {
    return false;
  }

  // A visibility event immediately after startup should not duplicate every request.
  // Queries without data can still fetch, while settled data gets a short freshness window.
  return query.state.dataUpdatedAt === 0 || Date.now() - query.state.dataUpdatedAt >= WINDOW_FOCUS_REFETCH_MIN_AGE_MS;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: shouldRefetchOnWindowFocus,
      gcTime: 1000 * 60 * 60 * 24 * 7, // 1 week
      // staleTime: 0,
      // gcTime: 0,
      // refetchOnMount: 'always',
      // refetchOnReconnect: 'always',
    },
  },
});
